// src/event.schema.ts
import { z } from "zod";
var shapeSchema = z.object({
  id: z.string(),
  type: z.enum(["circle", "polygon", "rect"]),
  // Propiedades para círculos
  x: z.number().optional(),
  y: z.number().optional(),
  radius: z.number().positive().optional(),
  // Propiedades para polígonos
  points: z.array(z.number()).optional(),
  // Propiedades para rectángulos
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  // Propiedades compartidas
  seatIdentifier: z.string().min(1).optional(),
  price: z.number().positive().optional(),
  rotation: z.number().optional(),
  fill: z.string().optional(),
  stroke: z.string().optional(),
  blocked: z.boolean().optional(),
  hidden: z.boolean().optional(),
  // Ocultar mesa en mapa público
  // Extensiones para mesas (opcional)
  tableId: z.string().min(1).optional(),
  tableLabel: z.string().min(1).optional(),
  capacity: z.number().int().positive().optional()
}).passthrough().superRefine((shape, ctx) => {
  const category = shape?.category;
  const isOverlay = category === "overlay";
  if (!isOverlay) {
    if (!shape.seatIdentifier || String(shape.seatIdentifier).trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "seatIdentifier es requerido para mesas",
        path: ["seatIdentifier"]
      });
    }
    if (shape.price === void 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "price es requerido para mesas",
        path: ["price"]
      });
    }
  }
});
var layoutDataSchema = z.object({
  shapes: z.array(shapeSchema),
  backgroundImage: z.string().url().optional(),
  dimensions: z.object({
    width: z.number().positive(),
    height: z.number().positive()
  }),
  metadata: z.object({
    version: z.string().default("1.0"),
    lastModified: z.string().datetime().optional()
  }).optional()
});
var createEventSchema = z.object({
  name: z.string().min(3, "El nombre debe tener al menos 3 caracteres").max(200),
  description: z.string().max(2e3).optional().or(z.literal("")),
  event_date: z.string().min(1, "La fecha es requerida"),
  // Hora de inicio del evento (formato HH:mm) - Requerido
  event_start_time: z.string().min(1, "La hora de inicio es requerida").regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, "Formato de hora inv\xE1lido (HH:mm)"),
  // Hora de fin del evento (formato HH:mm) - Requerido, para calcular duración
  event_end_time: z.string().min(1, "La hora de fin es requerida").regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, "Formato de hora inv\xE1lido (HH:mm)"),
  venue_name: z.string().min(2, "El nombre del venue es requerido"),
  venue_address: z.string().optional().or(z.literal("")),
  venue_image_url: z.string().url("URL inv\xE1lida").optional().or(z.literal("")),
  is_published: z.boolean().optional(),
  // Límite de confirmación de pago en horas hábiles. NULL = sin límite
  // Opciones válidas: 8 (1 día hábil), 40 (5 días hábiles), 56 (7 días hábiles), 120 (15 días hábiles)
  payment_confirmation_deadline_hours: z.number({ invalid_type_error: "El l\xEDmite debe ser un n\xFAmero de horas h\xE1biles" }).int("El l\xEDmite debe ser un n\xFAmero entero de horas").positive("El l\xEDmite debe ser mayor a 0").refine((val) => [8, 40, 56, 120].includes(val), {
    message: "Selecciona una opci\xF3n v\xE1lida: 1, 5, 7 o 15 d\xEDas h\xE1biles (8, 40, 56 o 120 horas)"
  }).nullable().optional()
});
var updateEventSchema = createEventSchema.partial();
var publishEventSchema = z.object({
  isPublished: z.boolean()
});
var updateLayoutSchema = z.object({
  layoutData: layoutDataSchema
});
var addEventOrganizerSchema = z.object({
  user_email: z.string().email("Debe ser un email v\xE1lido")
});
var exportLogSchema = z.object({
  exportType: z.enum(["image", "clipboard", "stats"]).describe("Tipo de exportaci\xF3n realizada"),
  scale: z.number().int().positive().optional().describe("Escala usada al exportar imagen"),
  source: z.enum(["seat_map", "map_editor"]).optional().describe("Origen de la exportaci\xF3n en el cliente"),
  viewport: z.object({
    x: z.number(),
    y: z.number(),
    width: z.number().positive(),
    height: z.number().positive()
  }).optional().describe("Viewport de la exportaci\xF3n cuando aplica"),
  stats: z.object({
    totalSeats: z.number().int().nonnegative().optional(),
    available: z.number().int().nonnegative().optional(),
    sold: z.number().int().nonnegative().optional(),
    locked: z.number().int().nonnegative().optional(),
    blocked: z.number().int().nonnegative().optional()
  }).optional().describe("Resumen de estad\xEDsticas exportadas cuando aplica")
});

// src/seat.schema.ts
import { z as z2 } from "zod";
var seatStatusSchema = z2.enum(["available", "locked", "sold", "blocked"]);
var createSeatSchema = z2.object({
  eventId: z2.string().uuid(),
  seatIdentifier: z2.string().min(1),
  price: z2.number().positive(),
  status: seatStatusSchema.default("available")
});
var lockSeatsSchema = z2.object({
  seatIds: z2.array(z2.string().uuid()).min(1, "Debes seleccionar al menos un asiento"),
  sessionId: z2.string().min(1),
  lockDurationMinutes: z2.number().positive().default(10)
});
var releaseSeatsSchema = z2.object({
  seatIds: z2.array(z2.string().uuid()),
  sessionId: z2.string()
});
var updateSeatStatusSchema = z2.object({
  status: seatStatusSchema
});
var generateSeatsFromLayoutSchema = z2.object({
  eventId: z2.string().uuid(),
  deleteExisting: z2.boolean().default(false)
});

// src/purchase.schema.ts
import { z as z3 } from "zod";
var paymentStatusSchema = z3.enum(["pending", "confirmed", "failed", "cancelled"]);
var createPurchaseSchema = z3.object({
  eventId: z3.string().uuid(),
  seatIds: z3.array(z3.string().uuid()).min(1, "Debes seleccionar al menos un asiento"),
  customerName: z3.string().min(2, "El nombre es requerido").max(200),
  customerEmail: z3.string().email("Email inv\xE1lido"),
  customerPhone: z3.string().min(1, "El tel\xE9fono es requerido").refine(
    (phone) => {
      const digitsOnly = phone.replace(/\D/g, "");
      let cleaned = digitsOnly;
      if (cleaned.length > 10 && cleaned.startsWith("52")) {
        cleaned = cleaned.substring(2);
      }
      return /^\d{10}$/.test(cleaned);
    },
    {
      message: "El tel\xE9fono debe tener exactamente 10 d\xEDgitos (solo n\xFAmeros, sin espacios ni caracteres especiales)"
    }
  ).transform((phone) => {
    const digitsOnly = phone.replace(/\D/g, "");
    let cleaned = digitsOnly;
    if (cleaned.length > 10 && cleaned.startsWith("52")) {
      cleaned = cleaned.substring(2);
    }
    return cleaned;
  }),
  customerIdNumber: z3.string().max(20).optional().or(z3.literal("")),
  customerType: z3.enum(["egresado_unach", "publico_general"], {
    required_error: "Debes seleccionar el tipo de cliente"
  }),
  // Programa educativo requerido solo para egresados UNACH
  graduateProgram: z3.string().optional().or(z3.literal("")),
  sessionId: z3.string().min(1),
  // Campo opcional para permitir registro automático de invitados desde checkout
  password: z3.string().min(6, "La contrase\xF1a debe tener al menos 6 caracteres").optional().or(z3.literal(""))
});
var checkoutFormBaseSchema = z3.object({
  customerName: z3.string().min(2, "El nombre debe tener al menos 2 caracteres").max(200),
  customerPhone: z3.string().min(1, "El tel\xE9fono es requerido").refine(
    (phone) => {
      const digitsOnly = phone.replace(/\D/g, "");
      let cleaned = digitsOnly;
      if (cleaned.length > 10 && cleaned.startsWith("52")) {
        cleaned = cleaned.substring(2);
      }
      return /^\d{10}$/.test(cleaned);
    },
    {
      message: "El tel\xE9fono debe tener exactamente 10 d\xEDgitos (solo n\xFAmeros, sin espacios ni caracteres especiales)"
    }
  ).transform((phone) => {
    const digitsOnly = phone.replace(/\D/g, "");
    let cleaned = digitsOnly;
    if (cleaned.length > 10 && cleaned.startsWith("52")) {
      cleaned = cleaned.substring(2);
    }
    return cleaned;
  }),
  customerIdNumber: z3.string().max(20).optional().or(z3.literal("")),
  customerType: z3.enum(["egresado_unach", "publico_general"], {
    required_error: "Debes seleccionar el tipo de cliente"
  }),
  // Programa educativo requerido solo para egresados UNACH
  graduateProgram: z3.string().optional().or(z3.literal(""))
});
var checkoutFormAuthenticatedSchema = checkoutFormBaseSchema.extend({
  customerEmail: z3.string().optional().or(z3.literal("")),
  password: z3.string().optional().or(z3.literal(""))
});
var checkoutFormGuestSchema = checkoutFormBaseSchema.extend({
  customerEmail: z3.string().email("Email inv\xE1lido"),
  password: z3.string().min(6, "La contrase\xF1a debe tener al menos 6 caracteres")
});
var checkoutFormSchema = z3.object({
  customerName: z3.string().min(2, "El nombre debe tener al menos 2 caracteres").max(200),
  customerEmail: z3.string().email("Email inv\xE1lido").optional().or(z3.literal("")),
  customerPhone: z3.string().min(1, "El tel\xE9fono es requerido").refine(
    (phone) => {
      const digitsOnly = phone.replace(/\D/g, "");
      let cleaned = digitsOnly;
      if (cleaned.length > 10 && cleaned.startsWith("52")) {
        cleaned = cleaned.substring(2);
      }
      return /^\d{10}$/.test(cleaned);
    },
    {
      message: "El tel\xE9fono debe tener exactamente 10 d\xEDgitos (solo n\xFAmeros, sin espacios ni caracteres especiales)"
    }
  ).transform((phone) => {
    const digitsOnly = phone.replace(/\D/g, "");
    let cleaned = digitsOnly;
    if (cleaned.length > 10 && cleaned.startsWith("52")) {
      cleaned = cleaned.substring(2);
    }
    return cleaned;
  }),
  customerIdNumber: z3.string().max(20).optional().or(z3.literal("")),
  customerType: z3.enum(["egresado_unach", "publico_general"], {
    required_error: "Debes seleccionar el tipo de cliente"
  }),
  // Programa educativo requerido solo para egresados UNACH
  graduateProgram: z3.string().optional().or(z3.literal("")),
  password: z3.string().min(6, "La contrase\xF1a debe tener al menos 6 caracteres").optional().or(z3.literal(""))
});
var uploadPaymentProofSchema = z3.object({
  paymentProofUrl: z3.string().url(),
  paymentProofFilename: z3.string().optional(),
  bankReference: z3.string().optional()
});
var updatePaymentStatusSchema = z3.object({
  paymentStatus: paymentStatusSchema,
  notes: z3.string().max(1e3).optional()
});
var purchaseFiltersSchema = z3.object({
  eventId: z3.string().uuid().optional(),
  paymentStatus: paymentStatusSchema.optional(),
  customerEmail: z3.string().email().optional(),
  startDate: z3.string().datetime().optional(),
  endDate: z3.string().datetime().optional()
});

// src/auth.schema.ts
import { z as z4 } from "zod";
var registerOrganizerSchema = z4.object({
  email: z4.string().email("Email inv\xE1lido"),
  password: z4.string().min(8, "La contrase\xF1a debe tener al menos 8 caracteres").regex(/[A-Z]/, "Debe contener al menos una may\xFAscula").regex(/[a-z]/, "Debe contener al menos una min\xFAscula").regex(/[0-9]/, "Debe contener al menos un n\xFAmero"),
  organizationName: z4.string().min(2, "El nombre de la organizaci\xF3n es requerido").max(200),
  phone: z4.string().optional().refine(
    (phone) => {
      if (!phone) return true;
      const digitsOnly = phone.replace(/\D/g, "");
      let cleaned = digitsOnly;
      if (cleaned.length > 10 && cleaned.startsWith("52")) {
        cleaned = cleaned.substring(2);
      }
      return /^\d{10}$/.test(cleaned);
    },
    {
      message: "El tel\xE9fono debe tener exactamente 10 d\xEDgitos (solo n\xFAmeros, sin espacios ni caracteres especiales)"
    }
  ).transform((phone) => {
    if (!phone) return void 0;
    const digitsOnly = phone.replace(/\D/g, "");
    let cleaned = digitsOnly;
    if (cleaned.length > 10 && cleaned.startsWith("52")) {
      cleaned = cleaned.substring(2);
    }
    return /^\d{10}$/.test(cleaned) ? cleaned : void 0;
  })
});
var loginSchema = z4.object({
  email: z4.string().email("Email inv\xE1lido"),
  password: z4.string().min(1, "La contrase\xF1a es requerida")
});
var updateOrganizerProfileSchema = z4.object({
  organizationName: z4.string().min(2).max(200).optional(),
  phone: z4.string().regex(/^\+?[1-9]\d{1,14}$/).optional()
});
var resetPasswordSchema = z4.object({
  email: z4.string().email("Email inv\xE1lido")
});
var changePasswordSchema = z4.object({
  currentPassword: z4.string().min(1),
  newPassword: z4.string().min(8).regex(/[A-Z]/, "Debe contener al menos una may\xFAscula").regex(/[a-z]/, "Debe contener al menos una min\xFAscula").regex(/[0-9]/, "Debe contener al menos un n\xFAmero")
});

// src/admin.schema.ts
import { z as z5 } from "zod";
var migrateEmailsSchema = z5.object({
  dryRun: z5.boolean().optional()
});
export {
  addEventOrganizerSchema,
  changePasswordSchema,
  checkoutFormAuthenticatedSchema,
  checkoutFormGuestSchema,
  checkoutFormSchema,
  createEventSchema,
  createPurchaseSchema,
  createSeatSchema,
  exportLogSchema,
  generateSeatsFromLayoutSchema,
  layoutDataSchema,
  lockSeatsSchema,
  loginSchema,
  migrateEmailsSchema,
  paymentStatusSchema,
  publishEventSchema,
  purchaseFiltersSchema,
  registerOrganizerSchema,
  releaseSeatsSchema,
  resetPasswordSchema,
  seatStatusSchema,
  shapeSchema,
  updateEventSchema,
  updateLayoutSchema,
  updateOrganizerProfileSchema,
  updatePaymentStatusSchema,
  updateSeatStatusSchema,
  uploadPaymentProofSchema
};
